/*======================================================================================================================
  * Bluebell: src/app/state/reports/reports.effects.ts
  * Last Edited: 12/09/2025
  * Copyright 2016-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import { selectCurrentProgrammeId } from '@state/programmes/programme.selectors';
import * as ReportsActions from '@state/reports/reports.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { Project } from '@state/projects/project.interfaces';
import { selectCurrentProjectId } from '@state/projects/project.selectors';
import { RollingReport, RollingReportComment } from '@state/reports/reports.interfaces';
import { selectShowingInactiveReports } from '@state/reports/reports.selectors';
import { filter, mergeMap, switchMap } from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';


@Injectable()
export class ReportEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private api = inject(ApiService);
  private handler = inject(EffectHandlerService);


  createReport$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.createReport),
    switchMap(({ report }) =>
      this.api.createReport(report).pipe(
        this.handler.effectResponse({
          success: (reportResponse: RollingReport) => ReportsActions.createReportSuccess({ report: reportResponse }),
          retry: ReportsActions.createReport({ report }),
          failure: ReportsActions.createReportFailure(),
          successMessage: `Report "${report.title}" created`,
          failureMessage: 'Could not create report'
        }),
    ))
  ));

  updateReport$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.updateReport),
    switchMap(({ report }) =>
      this.api.updateReport(report).pipe(
        this.handler.effectResponse({
          success: (reportResponse: RollingReport) => ReportsActions.updateReportSuccess({ report: reportResponse }),
          retry: ReportsActions.updateReport({ report }),
          failure: ReportsActions.updateReportFailure(),
          successMessage: 'Report updated',
          failureMessage: 'Could not update report',
        }),
    ))
  ));

  loadReport$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.loadReport),
    switchMap(({ uuid }) =>
      this.api.retrieveReport(uuid).pipe(
        this.handler.effectResponse({
          success: (reportResponse: RollingReport) => ReportsActions.loadReportSuccess({ report: reportResponse }),
          retry: ReportsActions.loadReport({ uuid }),
          failure: ReportsActions.loadReportFailure(),
          failureMessage: 'Could not load report data'
        }),
    ))
  ));

  deleteReport$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.deleteReport),
    mergeMap(({ uuid }) =>
      this.api.deleteReport(uuid).pipe(
        this.handler.effectResponse({
          success: () => ReportsActions.deleteReportSuccess({ uuid }),
          retry: ReportsActions.deleteReport({ uuid }),
          failure: ReportsActions.deleteReportFailure(),
          successMessage: 'Report deleted',
          failureMessage: 'Could not delete report'
        }),
    ))
  ));

  createReportComment$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.createReportComment),
    switchMap(({ comment }) =>
      this.api.createReportComment(comment).pipe(
        this.handler.effectResponse({
          success: (reportComment: RollingReportComment) =>
            ReportsActions.createReportCommentSuccess({ comment: reportComment }),
          retry: ReportsActions.createReportComment({ comment }),
          failure: ReportsActions.createReportCommentFailure()
        })
      ))
  ));

  updateReportComment$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.updateReportComment),
    switchMap(({ comment }) =>
      this.api.updateReportComment(comment).pipe(
        this.handler.effectResponse({
          success: (reportComment: RollingReportComment) =>
            ReportsActions.updateReportCommentSuccess({ comment: reportComment }),
          retry: ReportsActions.updateReportComment({ comment }),
          failure: ReportsActions.updateReportCommentFailure()
        })
      ))
  ));

  deleteReportComment$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.deleteReportComment),
    mergeMap(({ id }) =>
      this.api.deleteReportComment(id).pipe(
        this.handler.effectResponse({
          success: (reportComments: RollingReportComment[]) =>
            ReportsActions.deleteReportCommentSuccess({ comments: reportComments }),
          retry: ReportsActions.deleteReportComment({ id }),
          failure: ReportsActions.deleteReportCommentFailure()
        })
      ))
  ));

  deleteAllReportComments$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.deleteAllReportComments),
    switchMap(({ uuid }) =>
      this.api.reportDeleteComments(uuid).pipe(
        this.handler.effectResponse({
          success: (response: RollingReport) => ReportsActions.deleteAllReportCommentsSuccess({ report: response }),
          retry: ReportsActions.deleteAllReportComments({ uuid }),
          failure: ReportsActions.deleteAllReportCommentsFailure(),
        })
      )
    )
  ));

  loadAllProjectReports$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.loadAllProjectReports),
    concatLatestFrom(() => this.store.select(selectCurrentProjectId)),
    filter((payload: [Action, string|null]): payload is [Action, string] => !!payload[1]),
    switchMap(([, projectId]) => {
      return this.api.listReportsResults(projectId).pipe(
        this.handler.effectResponse({
          success: (reports: RollingReport[]) => ReportsActions.loadAllProjectReportsSuccess({ reports }),
          retry: ReportsActions.loadAllProjectReports(),
          failure: ReportsActions.loadAllProjectReportsFailure(),
          failureMessage: 'Could not load reports'
        })
      );
    })
  ));

  loadAllReports$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.loadAllReports, ReportsActions.toggleInactiveProjects),
    concatLatestFrom(() => [
      this.store.select(selectShowingInactiveReports),
      this.store.select(selectCurrentProgrammeId)
    ]),
    switchMap(([, showInactive, programme]) => {
      return this.api.listReports(showInactive, programme ?? '').pipe(
        this.handler.effectResponse({
          success: (reports: RollingReport[]) => ReportsActions.loadAllReportsSuccess({ reports }),
          retry: ReportsActions.loadAllReports(),
          failure: ReportsActions.loadAllReportsFailure(),
          failureMessage: 'Could not load reports'
        })
      );
    })
  ));

  loadAllProjects$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.loadAllProjects),
    concatLatestFrom(() => [
      this.store.select(selectCurrentProgrammeId),
      this.store.select(selectShowingInactiveReports)
    ]),
    filter((payload): payload is [ReturnType<typeof ReportsActions.loadAllProjects>, string, boolean] => !!payload[1]),
    switchMap(([, programmeId, showInactive]) => {
      return this.api.listProjects({ project_list: true, selected_programme: programmeId }, !showInactive || null).pipe(
        this.handler.effectResponse({
          success: (projects: Project[]) => ReportsActions.loadAllProjectsSuccess({ projects }),
          retry: ReportsActions.loadAllProjects(),
          failure: ReportsActions.loadAllProjectsFailure(),
          failureMessage: 'Could not load projects'
        })
      );
    })
  ));
}
